import { Button, Label, Modal, Skeleton } from "@indigo-components";
import { FC, useEffect, useState } from "react";
import {
  type AttachmentMeta,
  fetchLongMessageText,
} from "~/pages/appointments/hooks/useAgentChat";
import "./LongMessageModal.css";

/** A long user message to show in full. Null closes the modal. */
export type LongMessageSource =
  /** Sent this session: the full text is still in memory. */
  | { text: string }
  /** Loaded from history: the row holds a preview, the text lives in S3. */
  | { attachment: AttachmentMeta };

interface LongMessageModalProps {
  source: LongMessageSource | null;
  onClose: () => void;
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; text: string }
  | { status: "error"; message: string };

export const LongMessageModal: FC<LongMessageModalProps> = ({
  source,
  onClose,
}) => {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
    if (!source) return;
    if ("text" in source) {
      setState({ status: "ready", text: source.text });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetchLongMessageText(source.attachment)
      .then((text) => {
        if (!cancelled) setState({ status: "ready", text });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          message:
            err instanceof Error ? err.message : "Could not load the message",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const handleCopy = () => {
    if (state.status !== "ready") return;
    void navigator.clipboard.writeText(state.text).then(() => setCopied(true));
  };

  return (
    <Modal
      open={source !== null}
      onCancel={onClose}
      footer={null}
      centered
      width={880}
      destroyOnHidden
      title={
        <Label size="lg" weight="semibold">
          Full message
        </Label>
      }
    >
      <div className="flex flex-col gap-4">
        {state.status === "loading" && <Skeleton lines={6} />}
        {state.status === "error" && (
          <div className="indy-long-message-modal__error">{state.message}</div>
        )}
        {state.status === "ready" && (
          <div className="indy-long-message-modal__body">{state.text}</div>
        )}
        <div className="flex justify-end items-center gap-2">
          <Button
            variant="secondary"
            size="md"
            onClick={handleCopy}
            disabled={state.status !== "ready"}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
